import { defineStore } from "pinia";
import {
  collection,
  query,
  getFirestore,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";
import { Ref, ref } from "vue";
import { userUserStore } from "../stores/user";
import { Producto, productoConverter } from "@fixeduy/restaurantestypes";
import { generarCampoBusqueda } from "../utils/generarCampoBusqueda";

export function productoFotoStoragePath(
  empresaId: string,
  productoId: string,
  fileName = "main.jpg"
): string {
  return `empresas/${empresaId}/productos/${productoId}/${fileName}`;
}

export const useProductosStore = defineStore("productos", () => {
  const db = getFirestore();
  const userStore = userUserStore();

  const productos: Ref<Producto[]> = ref([]);
  const cargando = ref(false);

  const getFromFirestore = async () => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return;

    cargando.value = true;
    try {
      const collectionRoute = `empresas/${empresaId}/productos`;
      const q = query(collection(db, collectionRoute)).withConverter(
        productoConverter
      );

      const querySnapshot = await getDocs(q);
      productos.value = querySnapshot.docs.map(d => d.data());
    } finally {
      cargando.value = false;
    }
  };

  const getById = (id: string): Producto | null =>
    productos.value.find(p => p.id === id) ?? null;

  const crearProducto = async (producto: Producto): Promise<string | null> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return null;

    const collectionRoute = `empresas/${empresaId}/productos`;
    const docRef = doc(
      collection(db, collectionRoute).withConverter(productoConverter)
    );

    const nuevo: Producto = {
      id: docRef.id,
      version: 1,
      nombre: producto.nombre.trim(),
      precioSinIva: producto.precioSinIva,
      precioConIva: producto.precioConIva,
      tasaIvaId: producto.tasaIvaId ?? null,
      tasaIvaNombre: producto.tasaIvaNombre ?? null,
      categoriaId: producto.categoriaId ?? null,
      categoriaNombre: producto.categoriaNombre ?? null,
      codigo: producto.codigo ?? null,
      observaciones: producto.observaciones ?? null,
      impresionSectores: producto.impresionSectores ?? [],
      // Evitar que reglas de Firestore bloqueen updates (ej. foto) si el UI envía falso.
      activo: true,
      fotoUrl: producto.fotoUrl ?? null,
      fotoStoragePath: producto.fotoStoragePath ?? null
    };

    cargando.value = true;
    try {
      await setDoc(docRef, nuevo);
      productos.value.push(nuevo);
      return docRef.id;
    } catch (error) {
      console.error("Error al crear producto:", error);
      return null;
    } finally {
      cargando.value = false;
    }
  };

  const updateProducto = async (
    productoId: string,
    datos: Partial<
      Pick<
        Producto,
        | "nombre"
        | "precioConIva"
        | "precioSinIva"
        | "activo"
        | "categoriaId"
        | "categoriaNombre"
        | "tasaIvaId"
        | "tasaIvaNombre"
        | "codigo"
        | "observaciones"
        | "impresionSectores"
        | "fotoUrl"
        | "fotoStoragePath"
      >
    >
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const existente = getById(productoId);
    if (!existente) return false;

    const docRef = doc(
      getFirestore(),
      `empresas/${empresaId}/productos`,
      productoId
    ).withConverter(productoConverter);

    try {
      const productoActualizado: Producto = {
        ...existente,
        ...datos
      };

      const buscableExactoPor: string[] = [];
      if (productoActualizado.nombre)
        buscableExactoPor.push(productoActualizado.nombre);
      if (productoActualizado.codigo)
        buscableExactoPor.push(productoActualizado.codigo);
      if (productoActualizado.categoriaNombre)
        buscableExactoPor.push(productoActualizado.categoriaNombre);
      if (productoActualizado.observaciones)
        buscableExactoPor.push(productoActualizado.observaciones);

      const query =
        buscableExactoPor.length > 0
          ? generarCampoBusqueda({
              buscableParcialPor: buscableExactoPor,
              buscableExactoPor: []
            })
          : [];

      await updateDoc(docRef, {
        ...datos,
        query
      });

      const idx = productos.value.findIndex(p => p.id === productoId);
      if (idx !== -1) {
        productos.value[idx] = {
          ...productos.value[idx],
          ...datos,
          query
        } as Producto;
      }

      return true;
    } catch (error) {
      console.error("Error al actualizar producto:", error);
      return false;
    }
  };

  const subirFotoProducto = async (
    productoId: string,
    productoNombre: string,
    file: File
  ): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const producto = getById(productoId);
    if (!producto) return false;

    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const fileName = `${productoNombre}.${ext}`;
    const path = productoFotoStoragePath(empresaId, productoId, fileName);
    const storage = getStorage();
    const ref = storageRef(storage, path);

    try {
      if (producto.fotoStoragePath) {
        const oldRef = storageRef(getStorage(), producto.fotoStoragePath);
        await deleteObject(oldRef).catch(() => {});
      }
      await uploadBytes(ref, file, { contentType: file.type || "image/jpeg" });
      const fotoUrl = await getDownloadURL(ref);
      const ok = await updateProducto(productoId, {
        fotoUrl,
        fotoStoragePath: path
      });
      return ok;
    } catch (error) {
      console.error("Error al subir foto del producto:", error);
      return false;
    }
  };

  const quitarFotoProducto = async (productoId: string): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const producto = getById(productoId);
    if (!producto) return false;

    // Si no hay foto, consideramos que ya está "quitada".
    if (!producto.fotoStoragePath) {
      const ok = await updateProducto(productoId, {
        fotoUrl: null,
        fotoStoragePath: null
      });
      return ok;
    }

    try {
      const storage = getStorage();
      const fileRef = storageRef(storage, producto.fotoStoragePath);
      await deleteObject(fileRef).catch(() => {});

      return await updateProducto(productoId, {
        fotoUrl: null,
        fotoStoragePath: null
      });
    } catch (error) {
      console.error("Error al quitar foto del producto:", error);
      return false;
    }
  };

  const eliminarProducto = async (productoId: string): Promise<boolean> => {
    const empresaId = userStore.empresa?.id;
    if (!empresaId) return false;

    const producto = getById(productoId);
    const docRef = doc(
      getFirestore(),
      `empresas/${empresaId}/productos`,
      productoId
    );

    try {
      if (producto?.fotoStoragePath) {
        const storage = getStorage();
        const fileRef = storageRef(storage, producto.fotoStoragePath);
        await deleteObject(fileRef).catch(() => {});
      }
      await deleteDoc(docRef);
      productos.value = productos.value.filter(p => p.id !== productoId);
      return true;
    } catch (error) {
      console.error("Error al eliminar producto:", error);
      return false;
    }
  };

  const init = async () => {
    await getFromFirestore();
  };

  return {
    productos,
    cargando,
    init,
    getFromFirestore,
    getById,
    crearProducto,
    updateProducto,
    eliminarProducto,
    subirFotoProducto,
    quitarFotoProducto
  };
});
